/* eslint-disable react-hooks/rules-of-hooks -- pre-existing violations unmasked by #1772; tracked + to be fixed in #1842. Do not add new conditional hooks here. */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
} from '@mui/material'
import { useList, useOne } from '@refinedev/core'
import { Fragment, useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReactMarkdown from 'react-markdown'

interface DiscountCodeRevisionProps {
  discountCodeID: number
}
interface DiffData {
  values_changed?: Record<
    string,
    {
      diff: string
      old_value: any
      new_value: any
    }
  >
  dictionary_item_removed?: string[]
  dictionary_item_added?: Record<string, any>
  iterable_item_removed?: Record<string, any>
  iterable_item_added?: Record<string, any>
  type_changes?: Record<string, any>
}

const DISCOUNT_FIELD_NAME_MAP: Record<string, string> = {
  id: 'ID',
  code: 'Code',
  description: 'Description',
  discout_type: 'Discount Type',
  discount_value: 'Discount Value',
  valid_from: 'Valid From',
  valid_until: 'Valid Until',
  duration_months: 'Duration (Months)',
  max_uses: 'Max Uses',
  active: 'Active',
  archived: 'Archived',
}

const fieldOrder: Record<string, number> = {
  id: 1,
  code: 2,
  description: 3,
  discount_type: 4,
  discount_value: 5,
  valid_from: 6,
  valid_until: 7,
  duration_months: 8,
  max_uses: 9,
  active: 10,
  archived: 11,
}

function translateFieldName(fieldPath: string): string {
  const fieldName = fieldPath.includes("root['")
    ? fieldPath.split("root['")[1]?.split("']")[0]
    : fieldPath

  const fieldMap = DISCOUNT_FIELD_NAME_MAP
  return fieldMap[fieldName] || fieldName
}

export default function DiscountCodeRevisions({
  discountCodeID,
}: DiscountCodeRevisionProps) {
  const [loading, setLoading] = useState(true)

  const { data: discountRevisionData, isLoading } = useList({
    resource: 'discount-code-revisions',
    meta: {
      discount_code_id: discountCodeID as number,
    },
    errorNotification: (error, values, resource) => {
      return {
        message: 'Discount Revisions Not Found',
        description:
          error?.message || `Error loading revisions for discount code`,
        type: 'error',
      }
    },
  })

  // debounce to avoid rendering data too quickly
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [discountCodeID])

  if (isLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!discountRevisionData || !Object.keys(discountRevisionData).length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
          No revision data found
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box padding={2}>
        <List>
          {Object.entries(discountRevisionData).map(([name, versions]) => (
            <RevisionsExpandable key={name} name={name} versions={versions} />
          ))}
        </List>
      </Box>
    </>
  )
}

function RevisionsExpandable({
  name,
  versions,
}: {
  name: string
  versions: any[]
}) {
  if (!versions || versions.length === 0) return null
  const sortedVersions = Object.values(versions).sort(
    (a, b) => b.version - a.version
  )

  if (sortedVersions.length === 0) return null

  return (
    <Box>
      <Typography variant="h5" sx={{ my: 1 }}>
        Latest Version
      </Typography>

      <VersionDetails version={sortedVersions[0]} />
      {sortedVersions.length > 1 && (
        <Box marginTop={5}>
          <Box>
            <Typography variant="h5" sx={{ my: 1 }}>
              Previous Versions
            </Typography>
          </Box>

          {sortedVersions.slice(1).map((version, index) => (
            <VersionDetails
              key={`version-${index}-${version.version}`}
              version={version}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

function VersionDetails({ version }: { version: any }) {
  if (version == null || version.diff == null) {
    return null
  }
  const diffData = version.diff

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold' }}>
          Version {version.version}
        </Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          {new Date(version.date).toLocaleString('en-NZ', { hour12: true })}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="subtitle1">
          <strong>Data Entry By:</strong> {version.data_entry_by}
        </Typography>
        <Typography variant="subtitle1" marginBottom={3}>
          <strong>Date:</strong>{' '}
          {new Date(version.date).toLocaleString('en-NZ', { hour12: true })}
        </Typography>
        <DiffSection diffData={diffData as DiffData} />
      </AccordionDetails>
    </Accordion>
  )
}

function renderUnifiedDiff(diff: string) {
  return (
    <Box
      component="pre"
      sx={{
        background: '#282a36',
        color: '#f8f8f2',
        padding: '10px',
        borderRadius: '4px',
        fontFamily: 'monospace',
        overflowX: 'wrap',
        whiteSpace: 'pre-wrap',
      }}
    >
      {diff.split('\n').map((line, index) => (
        <Typography
          key={index}
          component="span"
          sx={{
            display: 'block',
            color: line.startsWith('-')
              ? '#ff5555' // Removed
              : line.startsWith('+')
              ? '#50fa7b' // Added
              : '#f8f8f2', // Context
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  )
}

function DiffSection({ diffData }: { diffData: DiffData }) {
  if (!diffData) return null

  const [showDiff, setShowDiff] = useState(false)

  const theme = useTheme()
  const lightOrDarkMode = theme.palette.mode

  function handleToggleDiff() {
    setShowDiff(!showDiff)
  }

  return (
    <Box marginBottom={4}>
      {/* Added or Changed Types (eg. New or removed db columns) */}
      {diffData.type_changes && (
        <Box marginBottom={2}>
          {Object.entries(diffData.type_changes).map(([key, value], index) =>
            value.old_type == "<class 'NoneType'>" ? (
              <Fragment key={`type-added-change-${index}-${key}`}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, textDecoration: 'underline' }}
                >
                  New Field Added
                </Typography>
                <Typography>
                  <strong>{translateFieldName(key)}</strong> field was added
                  with value: {JSON.stringify(value.new_value)}
                </Typography>
              </Fragment>
            ) : value.new_type == "<class 'NoneType'>" ? (
              <Fragment key={`type-removed-change-${index}-${key}`}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, textDecoration: 'underline' }}
                >
                  New Field Removed
                </Typography>
                <Typography>
                  <strong>{translateFieldName(key)}</strong> field was removed
                  with value: {JSON.stringify(value.old_value)}
                </Typography>
              </Fragment>
            ) : null
          )}
        </Box>
      )}
      {/* Removed Items */}
      {diffData.dictionary_item_removed &&
        diffData.dictionary_item_removed.length > 0 && (
          <Box marginBottom={2}>
            <Typography
              variant="h6"
              sx={{ mb: 1, textDecoration: 'underline' }}
            >
              Removed Items
            </Typography>
            {diffData.dictionary_item_removed?.map(
              (item: string, index: number) => (
                <Typography key={`removed-item-${item}-${index}`}>
                  <strong>{translateFieldName(item)}</strong> field was removed.
                </Typography>
              )
            )}
          </Box>
        )}

      {/* Added Items */}
      {diffData.dictionary_item_added && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Added Items
          </Typography>
          {Object.entries(diffData.dictionary_item_added).map(
            ([key, value], index) => (
              <Typography key={`added-item-${key}-${index}`}>
                <strong>{translateFieldName(key)}</strong> field was added with
                value: {JSON.stringify(value)}
              </Typography>
            )
          )}
        </Box>
      )}

      {/* Values Changed */}
      {diffData.values_changed && (
        <Box marginBottom={2}>
          {Object.entries(diffData.values_changed).map(
            ([key, change], index) => (
              <Box
                key={`changed-item-${key}-${index}-${change}`}
                marginBottom={2}
              >
                {key === 'root' ? (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      <strong>New Entry</strong>
                    </Typography>
                    <Box
                      bgcolor={lightOrDarkMode === 'light' ? '#f5f5f5' : '#333'}
                      borderRadius={1}
                      padding={2}
                      mb={2}
                    >
                      {Object.entries(change.new_value || {})
                        .sort(([fieldA], [fieldB]) => {
                          return (
                            (fieldOrder[fieldA] || Infinity) -
                            (fieldOrder[fieldB] || Infinity)
                          )
                        })
                        .map(([field, value], fieldIndex) => (
                          <Box
                            key={`${value}-${field}-${fieldIndex}`}
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2">
                              <strong>{translateFieldName(field)}</strong>
                            </Typography>
                            {
                              <>
                                {String(value) !== 'null' &&
                                String(value) !== '' ? (
                                  <ReactMarkdown
                                    className={`markdown-${lightOrDarkMode}`}
                                  >
                                    {typeof value === 'boolean'
                                      ? value === true
                                        ? 'Yes'
                                        : 'No'
                                      : String(value)}
                                  </ReactMarkdown>
                                ) : (
                                  'None'
                                )}
                              </>
                            }
                          </Box>
                        ))}
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ mb: 1, textDecoration: 'underline' }}
                    >
                      Changed Values
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>{translateFieldName(key)}</strong> field was
                      changed:
                    </Typography>
                    <Typography variant="body2">
                      <strong>New Value</strong>
                    </Typography>
                    <Box
                      bgcolor="#f5f5f5"
                      borderRadius={1}
                      paddingX={2}
                      paddingY={1}
                      mb={2}
                    >
                      {typeof change.new_value === 'string' ? (
                        <ReactMarkdown
                          className={`markdown-${lightOrDarkMode}`}
                        >
                          {change.new_value}
                        </ReactMarkdown>
                      ) : typeof change.new_value === 'boolean' ? (
                        change.new_value === true ? (
                          'Yes'
                        ) : (
                          'No'
                        )
                      ) : (
                        JSON.stringify(change.new_value)
                      )}
                    </Box>
                    <Typography variant="body2">
                      <strong>Old Value</strong>
                    </Typography>
                    <Box
                      bgcolor="#f5f5f5"
                      borderRadius={1}
                      paddingX={2}
                      paddingY={1}
                      mb={2}
                    >
                      {typeof change.old_value === 'string' ? (
                        <ReactMarkdown
                          className={`markdown-${lightOrDarkMode}`}
                        >
                          {change.old_value}
                        </ReactMarkdown>
                      ) : typeof change.old_value === 'boolean' ? (
                        change.old_value === true ? (
                          'Yes'
                        ) : (
                          'No'
                        )
                      ) : (
                        JSON.stringify(change.old_value)
                      )}
                    </Box>
                    {change.diff && (
                      <FormControlLabel
                        control={<Switch onChange={handleToggleDiff} />}
                        label="Show Diff View"
                      />
                    )}
                  </>
                )}
                {change.diff && showDiff && renderUnifiedDiff(change.diff)}
              </Box>
            )
          )}
        </Box>
      )}

      {/* Removed Iterable Items */}
      {diffData.iterable_item_removed && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Removed Items
          </Typography>
          {Object.entries(diffData.iterable_item_removed).map(
            ([key, value], index) => (
              <Typography key={`removed-item-${key}-${index}`}>
                <strong>{translateFieldName(key)}</strong> field was removed
                with value: {JSON.stringify(value)}
              </Typography>
            )
          )}
        </Box>
      )}

      {/* Added Iterable Items */}
      {diffData.iterable_item_added && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Added Items
          </Typography>
          {Object.entries(diffData.iterable_item_added).map(
            ([key, value], index) => (
              <Typography key={`added-item-${key}-${index}`}>
                <strong>{translateFieldName(key)}</strong> field was added with
                value: {JSON.stringify(value) || 'None'}
              </Typography>
            )
          )}
        </Box>
      )}
    </Box>
  )
}
